/**
 * ContratosASuspenderModal (v1.5.0-rc5) — vista previa de servicios que
 * se suspenderán en la próxima corrida de cobranza.
 *
 * Disparado desde:
 *   - Alerta "X servicios se suspenderán..." en el dashboard.
 *   - Botón opcional "Ver lista" desde la página de cobranza.
 *
 * Llama a `GET /api/v1/dashboard/contratos-a-suspender?dias_gracia=N`
 * que ya excluye clientes con promesa de pago vigente.
 *
 * No ejecuta la suspensión — solo informa. La ejecución vive en
 * `/cobranza` para que el operador la dispare conscientemente.
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  AlertTriangle,
  ArrowRight,
  Calendar,
  CheckCircle2,
  Loader2,
  Receipt,
} from "lucide-react";
import { Dialog } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { apiCall } from "@/lib/api";
import { formatMXN } from "@/lib/utils";

type ContratoASuspender = {
  contrato_id: string;
  codigo: string;
  cliente_id: string;
  cliente_codigo: string;
  cliente_nombre: string;
  plan_id: string | null;
  plan_nombre: string;
  monto_vencido: string;
  factura_mas_antigua: string | null;
  dias_vencida_max: number;
  facturas_vencidas_count: number;
};

type Response = {
  dias_gracia: number;
  preview_para: string; // v1.5.0-rc6: fecha simulada (today + 1)
  total: number;
  monto_total_vencido: string;
  items: ContratoASuspender[];
};

export function ContratosASuspenderModal({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  // dias_gracia ajustable — null = usa default global del config_sistema.
  // Útil para simular escenarios "¿qué pasaría si bajo gracia a 0?".
  const [diasGracia, setDiasGracia] = useState<number | null>(null);

  const { data, isLoading, isError } = useQuery<Response>({
    queryKey: ["contratos-a-suspender", diasGracia],
    queryFn: () => {
      const qs = diasGracia !== null ? `?dias_gracia=${diasGracia}` : "";
      return apiCall<Response>("get", `/api/v1/dashboard/contratos-a-suspender${qs}`);
    },
    enabled: open,
    refetchInterval: open ? 60_000 : false,
  });

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()} size="xl">
      <div className="flex items-center justify-between border-b p-4 pr-12">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-lg bg-amber-100 dark:bg-amber-500/15 flex items-center justify-center">
            <AlertTriangle size={18} className="text-amber-700 dark:text-amber-400" />
          </div>
          <div>
            <h2 className="text-base font-semibold">Servicios por suspender mañana</h2>
            <p className="text-xs text-muted-foreground">
              {data
                ? `${data.total} contrato${data.total === 1 ? "" : "s"} · gracia ${data.dias_gracia}d · total ${formatMXN(Number(data.monto_total_vencido))} · preview para ${formatPreviewDate(data.preview_para)}`
                : "cargando vista previa…"}
            </p>
          </div>
        </div>
        {/* X close ahora viene del Dialog component (built-in, esquina superior derecha) */}
      </div>

      {/* Filtro de gracia ajustable. Cambiar el valor dispara refetch automático
          vía cambio de queryKey, no necesitamos botón "Recalcular" explícito. */}
      <div className="border-b px-4 py-2.5 flex items-center gap-3 bg-muted/30 flex-wrap">
        <span className="text-xs text-muted-foreground">Simular con gracia:</span>
        {[null, 0, 3, 5, 7, 10].map((g) => (
          <button
            key={String(g)}
            onClick={() => setDiasGracia(g)}
            className={`text-xs px-2.5 py-1 rounded-md border transition-colors ${
              diasGracia === g
                ? "bg-primary text-primary-foreground border-primary"
                : "bg-background border-border hover:bg-accent"
            }`}
          >
            {g === null ? "Default" : `${g} días`}
          </button>
        ))}
      </div>

      {/* Body */}
      <div className="max-h-[60vh] overflow-y-auto">
        {isLoading ? (
          <div className="py-12 text-center text-sm text-muted-foreground">
            <Loader2 size={20} className="mx-auto mb-2 animate-spin" />
            calculando…
          </div>
        ) : isError ? (
          <div className="py-12 text-center text-sm text-destructive">
            Error al cargar — intenta refrescar
          </div>
        ) : !data || data.items.length === 0 ? (
          <div className="py-12 text-center">
            <CheckCircle2 size={32} className="mx-auto mb-3 text-emerald-500" />
            <p className="text-sm font-medium">Nada por suspender mañana</p>
            <p className="text-xs text-muted-foreground mt-1">
              Ningún contrato cumple criterios de suspensión para el{" "}
              {data ? formatPreviewDate(data.preview_para) : "día siguiente"} con gracia{" "}
              {data?.dias_gracia ?? "default"}d.
            </p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-muted/80 backdrop-blur text-[10px] uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="text-left px-4 py-2 font-medium">Cliente</th>
                <th className="text-left px-3 py-2 font-medium">Plan</th>
                <th className="text-right px-3 py-2 font-medium">Vencido</th>
                <th className="text-center px-3 py-2 font-medium">Días</th>
                <th className="text-center px-3 py-2 font-medium">Facturas</th>
                <th className="text-right px-4 py-2 font-medium">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((it) => (
                <tr
                  key={it.contrato_id}
                  className="border-t hover:bg-accent/30 transition-colors"
                >
                  <td className="px-4 py-2.5">
                    <Link
                      to={`/clientes/${it.cliente_id}`}
                      className="hover:underline"
                    >
                      <div className="font-medium">{it.cliente_nombre}</div>
                      <div className="text-[10px] text-muted-foreground tabular-nums">
                        {it.cliente_codigo} · {it.codigo}
                      </div>
                    </Link>
                  </td>
                  <td className="px-3 py-2.5 text-xs text-muted-foreground">
                    {it.plan_nombre || "—"}
                  </td>
                  <td className="px-3 py-2.5 text-right tabular-nums font-semibold">
                    {formatMXN(Number(it.monto_vencido))}
                  </td>
                  <td className="px-3 py-2.5 text-center">
                    <span
                      className={`tabular-nums text-xs font-semibold ${
                        it.dias_vencida_max > 14
                          ? "text-red-600 dark:text-red-400"
                          : it.dias_vencida_max > 7
                          ? "text-amber-600 dark:text-amber-400"
                          : "text-foreground"
                      }`}
                    >
                      {it.dias_vencida_max}d
                    </span>
                  </td>
                  <td className="px-3 py-2.5 text-center text-xs text-muted-foreground tabular-nums">
                    {it.facturas_vencidas_count}
                  </td>
                  <td className="px-4 py-2.5">
                    <div className="flex items-center justify-end gap-1">
                      <Link
                        to={`/facturas?cliente_id=${it.cliente_id}&estatus=vencida`}
                        title="Ver facturas vencidas"
                        className="p-1.5 rounded-md hover:bg-accent text-muted-foreground hover:text-foreground"
                      >
                        <Receipt size={13} />
                      </Link>
                      <Link
                        to={`/promesas?cliente_id=${it.cliente_id}`}
                        title="Registrar promesa de pago"
                        className="p-1.5 rounded-md hover:bg-accent text-muted-foreground hover:text-foreground"
                      >
                        <Calendar size={13} />
                      </Link>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Helper local: formato corto de fecha (15-may-2026) */}
      {/* (definición abajo al final del archivo) */}

      {/* Footer */}
      <div className="border-t p-3 flex items-center justify-between bg-muted/30">
        <p className="text-[11px] text-muted-foreground">
          Esta vista no ejecuta nada. Para suspender masivamente, usa{" "}
          <Link to="/cobranza" className="underline hover:text-foreground">
            /cobranza
          </Link>
          .
        </p>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={onClose}>
            Cerrar
          </Button>
          {data && data.total > 0 && (
            <Link to="/cobranza" onClick={onClose}>
              <Button size="sm" className="gap-1">
                Ir a cobranza
                <ArrowRight size={12} />
              </Button>
            </Link>
          )}
        </div>
      </div>
    </Dialog>
  );
}

// v1.5.0-rc6: helper para mostrar la fecha de preview en formato legible.
function formatPreviewDate(iso: string | undefined): string {
  if (!iso) return "";
  const d = new Date(iso + "T00:00:00");
  return d.toLocaleDateString("es-MX", {
    weekday: "long",
    day: "2-digit",
    month: "short",
  });
}
